import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, InputAdornment, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { getRepairCustomers } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';
import ExportButtons from '../../components/ExportButtons';
import { dash, fmtExportDateTime, fmtExportMoney } from '../../utils/tableExport';

const money = (v) =>
  parseFloat(v || 0).toLocaleString('en-LK', { style: 'currency', currency: 'LKR' });

const fmtWhen = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const RepairCustomers = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async (q = '') => {
    try {
      setLoading(true);
      setError('');
      const res = await getRepairCustomers(q ? { search: q } : {});
      setRows(res.data || []);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load repair customers'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const onBranch = () => load(search);
    window.addEventListener('branch-filter-changed', onBranch);
    return () => window.removeEventListener('branch-filter-changed', onBranch);
  }, [load]);

  const onSearch = (e) => {
    e.preventDefault();
    load(search.trim());
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Customer profiles</Typography>
          <Typography variant="body2" color="text.secondary">
            Every customer who brought a repair — jobs, spend, and repeat items.
          </Typography>
        </Box>
        <ExportButtons
          title="Repair Customers"
          filename="repair_customers"
          columns={[
            { label: 'Customer', value: (r) => dash(r.customer_name) },
            { label: 'Phone', value: (r) => dash(r.customer_phone) },
            { label: 'Jobs', value: (r) => r.job_count },
            { label: 'Total billed', value: (r) => fmtExportMoney(r.total_cost) },
            { label: 'Paid', value: (r) => fmtExportMoney(r.total_paid) },
            { label: 'Balance', value: (r) => fmtExportMoney(r.balance_due) },
            { label: 'Last job', value: (r) => fmtExportDateTime(r.last_job_at) }
          ]}
          rows={rows}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box component="form" onSubmit={onSearch} sx={{ mb: 2 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search name, phone, or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            )
          }}
        />
      </Box>

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell align="right">Jobs</TableCell>
                <TableCell align="right">Billed</TableCell>
                <TableCell align="right">Paid</TableCell>
                <TableCell>Last job</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography color="text.secondary">No repair customers found.</Typography>
                  </TableCell>
                </TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.customer_id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/repairs/customers/${r.customer_id}`)}>
                  <TableCell>
                    <Typography fontWeight={600}>{r.customer_name || '—'}</Typography>
                    {r.customer_email && (
                      <Typography variant="caption" color="text.secondary">{r.customer_email}</Typography>
                    )}
                  </TableCell>
                  <TableCell>{r.customer_phone || '—'}</TableCell>
                  <TableCell align="right"><Chip size="small" label={r.job_count} /></TableCell>
                  <TableCell align="right">{money(r.total_cost)}</TableCell>
                  <TableCell align="right">{money(r.total_paid)}</TableCell>
                  <TableCell>{fmtWhen(r.last_job_at)}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={(e) => { e.stopPropagation(); navigate(`/repairs/customers/${r.customer_id}`); }}>
                      View profile
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default RepairCustomers;
